'use client';

import { useState } from 'react';
import { Globe, BookOpen, GraduationCap, Info, X } from 'lucide-react';
import { AUDIO_BOOK_LANGUAGES, AudioBookLanguage } from '@/lib/site-config';

interface StepLanguageProps {
  primaryLanguage: AudioBookLanguage;
  onPrimaryLanguageChange: (lang: AudioBookLanguage) => void;
  bilingualMode: boolean;
  onBilingualModeChange: (enabled: boolean) => void;
  secondaryLanguage?: AudioBookLanguage;
  onSecondaryLanguageChange: (lang: AudioBookLanguage) => void;
  montessoriMode: boolean;
  onMontessoriModeChange: (enabled: boolean) => void;
  isSubscriber: boolean;
  isPro?: boolean;
  onNext?: () => void;
}

export default function StepLanguage({
  primaryLanguage,
  onPrimaryLanguageChange,
  bilingualMode,
  onBilingualModeChange,
  secondaryLanguage,
  onSecondaryLanguageChange,
  montessoriMode,
  onMontessoriModeChange,
  isSubscriber,
  isPro = false,
  onNext,
}: StepLanguageProps) {
  const [showLearnMore, setShowLearnMore] = useState(false);
  const [showUpsellModal, setShowUpsellModal] = useState(false);
  const [upsellMessage, setUpsellMessage] = useState('');

  const handleLanguageSelect = (langValue: AudioBookLanguage) => {
    const lang = AUDIO_BOOK_LANGUAGES.find(l => l.value === langValue);
    if (lang && lang.proOnly && !isSubscriber) {
      setUpsellMessage('This language requires a Pro subscription. Please upgrade to unlock!');
      setShowUpsellModal(true);
      return;
    }
    onPrimaryLanguageChange(langValue);
  };

  const handleBilingualToggle = () => {
    if (!isSubscriber && !bilingualMode) {
      setUpsellMessage('Bilingual mode requires a Pro subscription. Please upgrade to unlock!');
      setShowUpsellModal(true);
      return;
    }
    onBilingualModeChange(!bilingualMode);
  };

  const handleMontessoriToggle = () => {
    if (!isPro && !montessoriMode) {
      setUpsellMessage('Montessori Learning is exclusive to Amaya Pro. Upgrade to unlock this premium feature!');
      setShowUpsellModal(true);
      return;
    }
    onMontessoriModeChange(!montessoriMode);
  };

  return (
    <div style={{ maxWidth: '100%', width: '100%', margin: '0 auto' }}>
      <div style={{ 
        textAlign: 'center', 
        marginBottom: '2.5rem',
        animation: 'fadeInUp 0.6s ease-out',
      }}>
        <h2 style={{ 
          fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', 
          fontWeight: 800, 
          color: '#444',
          marginBottom: '0.75rem',
        }}>
          Choose Your Language
        </h2>
        <p style={{ 
          fontSize: '1.125rem', 
          color: '#666',
          lineHeight: 1.6,
        }}>
          Create audio picture books in 13 languages with optional bilingual learning
        </p>
      </div>

      {/* Primary Language Selection */}
      <div style={{ marginBottom: '3rem' }}>
        <h3 style={{
          fontSize: '1.25rem',
          fontWeight: 700,
          color: '#444',
          marginBottom: '1.25rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
        }}>
          <Globe size={24} color="#C5B4FF" />
          Primary Language
        </h3>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
          gap: '0.75rem',
        }}>
          {AUDIO_BOOK_LANGUAGES.map((lang) => {
            const isSelected = primaryLanguage === lang.value;
            const isLocked = lang.proOnly && !isSubscriber;

            return (
              <button
                key={lang.value}
                onClick={() => handleLanguageSelect(lang.value)}
                disabled={isLocked}
                style={{
                  padding: '0.75rem',
                  background: isSelected ? 'linear-gradient(135deg, rgba(197, 180, 255, 0.2) 0%, rgba(181, 242, 224, 0.2) 100%)' : 'white',
                  border: isSelected ? '3px solid #C5B4FF' : '2px solid #e0d5f0',
                  borderRadius: 'var(--r-lg, 16px)',
                  cursor: isLocked ? 'not-allowed' : 'pointer',
                  opacity: isLocked ? 0.5 : 1,
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.375rem',
                  minHeight: '90px',
                  position: 'relative',
                }}
                onMouseEnter={(e) => {
                  if (!isLocked) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(197, 180, 255, 0.25)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                aria-label={`Select ${lang.label} language`}
                aria-pressed={isSelected}
              >
                <div style={{ fontSize: '1.5rem', lineHeight: 1 }}>{lang.flag}</div>
                <div style={{
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: '#444',
                  textAlign: 'center',
                  lineHeight: 1.2,
                }}>
                  {lang.label}
                </div>
                {isLocked && (
                  <div style={{
                    fontSize: '0.6875rem',
                    color: '#7C3AED',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}>
                    Pro
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Bilingual Mode Toggle */}
      <div style={{
        background: 'white',
        borderRadius: 'var(--r-2xl, 28px)',
        padding: '1.5rem',
        border: '2px solid #e0d5f0',
        marginBottom: '2rem',
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '0.75rem',
        }}>
          <h3 style={{
            fontSize: '1.125rem',
            fontWeight: 700,
            color: '#444',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            margin: 0,
          }}>
            <BookOpen size={20} color="#C5B4FF" />
            Bilingual Learning Mode
            {!isSubscriber && (
              <span style={{
                fontSize: '0.6875rem',
                padding: '0.25rem 0.5rem',
                background: 'linear-gradient(135deg, #8B5CF6, #A78BFA)',
                color: 'white',
                borderRadius: '8px',
                fontWeight: 700,
              }}>
                PRO
              </span>
            )}
          </h3>

          <button
            onClick={handleBilingualToggle}
            role="switch"
            aria-checked={bilingualMode}
            aria-label="Toggle bilingual mode"
            style={{
              width: '56px',
              height: '32px',
              borderRadius: '16px',
              background: bilingualMode ? '#C5B4FF' : '#e0d5f0',
              border: 'none',
              cursor: 'pointer',
              position: 'relative',
              transition: 'background 0.3s ease',
            }}
          >
            <div style={{
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              background: 'white',
              position: 'absolute',
              top: '4px',
              left: bilingualMode ? '28px' : '4px',
              transition: 'left 0.3s ease',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
            }} />
          </button>
        </div>

        <p style={{
          fontSize: '0.9375rem',
          color: '#666',
          margin: 0,
          lineHeight: 1.5,
        }}>
          Display text in two languages side-by-side for immersive learning
        </p>

        {bilingualMode && (
          <div style={{ marginTop: '1rem' }}>
            <label style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: 600,
              color: '#444',
              marginBottom: '0.5rem',
            }}>
              Second Language:
            </label>
            <select
              value={secondaryLanguage}
              onChange={(e) => onSecondaryLanguageChange(e.target.value as AudioBookLanguage)}
              style={{
                width: '100%',
                padding: '0.75rem',
                borderRadius: '12px',
                border: '2px solid #e0d5f0',
                fontSize: '1rem',
                fontFamily: 'inherit',
                cursor: 'pointer',
              }}
              aria-label="Select secondary language for bilingual mode"
            >
              {AUDIO_BOOK_LANGUAGES.map((lang) => (
                <option key={lang.value} value={lang.value}>
                  {lang.flag} {lang.label}
                </option>
              ))}
            </select>

            {/* Bilingual Preview */}
            <div style={{
              marginTop: '1rem',
              padding: '1rem',
              background: 'rgba(197, 180, 255, 0.05)',
              borderRadius: '12px',
              border: '1px dashed #C5B4FF',
            }}>
              <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#7C3AED', marginBottom: '0.5rem' }}>
                Preview:
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '1rem',
                fontSize: '0.875rem',
              }}>
                <div>
                  <div style={{ fontWeight: 600, color: '#444', marginBottom: '0.25rem' }}>
                    {AUDIO_BOOK_LANGUAGES.find(l => l.value === primaryLanguage)?.label}
                  </div>
                  <div style={{ color: '#666' }}>
                    Once upon a time...
                  </div>
                </div>
                <div>
                  <div style={{ fontWeight: 600, color: '#444', marginBottom: '0.25rem' }}>
                    {AUDIO_BOOK_LANGUAGES.find(l => l.value === secondaryLanguage)?.label}
                  </div>
                  <div style={{ color: '#666' }}>
                    {secondaryLanguage === 'es' && 'Érase una vez...'}
                    {secondaryLanguage === 'de' && 'Es war einmal...'}
                    {secondaryLanguage === 'fr' && 'Il était une fois...'}
                    {!['es', 'de', 'fr'].includes(secondaryLanguage || '') && 'Translation...'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>


      {/* Next Button */}
      {onNext && (
        <div style={{
          marginTop: '3rem',
          paddingTop: '2rem',
          borderTop: '2px solid rgba(197, 180, 255, 0.15)',
          textAlign: 'center',
        }}>
          <button
            type="button"
            onClick={onNext}
            className="premium-continue-btn"
            style={{
              width: '100%',
              maxWidth: '450px',
              padding: '1.375rem 2.5rem',
              background: 'linear-gradient(135deg, #C5B4FF 0%, #B5F2E0 50%, #A8D8E6 100%)',
              color: '#1a1a1a',
              border: 'none',
              borderRadius: '28px',
              fontSize: '1.1875rem',
              fontWeight: 900,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.875rem',
              margin: '0 auto',
              boxShadow: '0 12px 40px rgba(197, 180, 255, 0.5), inset 0 2px 12px rgba(255, 255, 255, 0.3), 0 0 0 2px rgba(255, 255, 255, 0.2)',
              transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
              position: 'relative',
              overflow: 'hidden',
              letterSpacing: '0.01em',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
              e.currentTarget.style.boxShadow = '0 16px 56px rgba(197, 180, 255, 0.6), inset 0 2px 16px rgba(255, 255, 255, 0.4), 0 0 0 3px rgba(255, 255, 255, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0) scale(1)';
              e.currentTarget.style.boxShadow = '0 12px 40px rgba(197, 180, 255, 0.5), inset 0 2px 12px rgba(255, 255, 255, 0.3), 0 0 0 2px rgba(255, 255, 255, 0.2)';
            }}
            aria-label="Continue to voice selection"
          >
            {/* Shimmer overlay */}
            <div style={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
              animation: 'shimmer 3s ease-in-out infinite',
              pointerEvents: 'none',
            }} />
            
            <span style={{ position: 'relative', zIndex: 1, textShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' }}>
              Continue to Voice Selection
            </span>
            <span 
              className="arrow-icon"
              style={{ 
                fontSize: '1.75rem', 
                position: 'relative', 
                zIndex: 1,
                transition: 'transform 0.3s ease',
                filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.15))',
              }}
            >
              →
            </span>
          </button>
        </div>
      )}

      {/* Upsell Modal */}
      {showUpsellModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '1rem',
          }}
          onClick={() => setShowUpsellModal(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '24px',
              padding: '2rem',
              maxWidth: '500px',
              width: '100%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              position: 'relative',
              animation: 'slideIn 0.3s ease-out',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setShowUpsellModal(false)}
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '1.5rem',
                color: '#999',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#f5f5f5';
                e.currentTarget.style.color = '#333';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'none';
                e.currentTarget.style.color = '#999';
              }}
              aria-label="Close"
            >
              <X size={20} />
            </button>

            {/* Icon */}
            <div
              style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.5rem',
                boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)',
              }}
            >
              <span style={{ fontSize: '2rem' }}>🔒</span>
            </div>

            {/* Title */}
            <h3
              style={{
                fontSize: '1.5rem',
                fontWeight: 800,
                color: '#2c3e50',
                textAlign: 'center',
                marginBottom: '1rem',
              }}
            >
              Premium Feature
            </h3>

            {/* Message */}
            <p
              style={{
                fontSize: '1.125rem',
                color: '#64748b',
                textAlign: 'center',
                lineHeight: 1.6,
                marginBottom: '2rem',
              }}
            >
              {upsellMessage}
            </p>

            {/* CTA Buttons */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <a
                href="/pricing"
                style={{
                  width: '100%',
                  padding: '1rem 2rem',
                  background: 'linear-gradient(135deg, #667eea, #764ba2)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '16px',
                  fontSize: '1.125rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  textDecoration: 'none',
                  boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)',
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 12px 32px rgba(102, 126, 234, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(102, 126, 234, 0.3)';
                }}
              >
                ✨ Upgrade to Pro
              </a>
              <button
                type="button"
                onClick={() => setShowUpsellModal(false)}
                style={{
                  width: '100%',
                  padding: '1rem 2rem',
                  background: 'transparent',
                  color: '#64748b',
                  border: '2px solid #e2e8f0',
                  borderRadius: '16px',
                  fontSize: '1rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f8fafc';
                  e.currentTarget.style.borderColor = '#cbd5e1';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.borderColor = '#e2e8f0';
                }}
              >
                Maybe Later
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }

        .premium-continue-btn:hover .arrow-icon {
          transform: translateX(6px);
        }

      `}</style>
      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}

